import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiFileText, FiAlertTriangle, FiSettings, FiPlus, FiChevronDown } from 'react-icons/fi';
import { LedgerAccountSelect } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import {
  createPaymentVoucher,
  getDonationCategories,
  getAllLedgerAccounts,
  getLedgerBalanceMap,
  getCategoryFund,
  getExpenseTypes,
  createExpenseType,
} from '../../services/api';
import { today } from '../../utils/dateUtils';
import {
  getCurrentUnlockedFinancialYear,
  getUnlockedFinancialYears,
  isOpeningFinancialYear,
} from '../../utils/financialYears';
import styles from './NewPaymentsPage.module.css';
import aStyles from '../Accounting/Accounting.module.css';
import jStyles from '../JournalEntries/JournalEntries.module.css';

import { fmt, fmtCurrency } from '../../utils/formatNumber';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

const PAYMENT_MODES = [
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
];

// -- Helpers -------------------------------------------------------------------

const getPreferredCreditAccount = (accounts, accountType) => {
  const filtered = (accounts || []).filter((account) => account.accountType === accountType);
  return filtered.find((account) => account.isSystem) || filtered[0] || null;
};

const modeForAccountType = (accountType, currentMode) => {
  if (accountType === 'cash') return 'cash';
  if (accountType === 'bank') return ['bank_transfer', 'cheque', 'upi'].includes(currentMode) ? currentMode : 'bank_transfer';
  return currentMode;
};

const fmtBal = (bal) => {
  if (bal === undefined || bal === null) return null;
  return { side: bal >= 0 ? 'Dr' : 'Cr', amount: fmt(Math.abs(bal)), color: bal >= 0 ? '#3b82f6' : '#ef4444' };
};

const FundSourceSelect = ({ value, onChange, categories, fundBalances, loadingFunds }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  const items = [
    ...categories.map((c) => ({ id: String(c.id), name: c.name, bal: fundBalances[String(c.id)] ?? null })),
    { id: '__commission__', name: 'Commission Fund', bal: fundBalances.__commission__ ?? null },
  ];

  const selected = items.find((item) => item.id === value) || null;

  return (
    <div ref={ref} className={jStyles.acctSelect}>
      <button
        type="button"
        className={jStyles.acctTrigger}
        style={!value ? { borderColor: 'var(--color-danger, #ef4444)' } : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`${jStyles.acctTriggerText} ${!selected ? jStyles.acctTriggerPlaceholder : ''}`}>
          {selected ? selected.name : '— Select fund source —'}
        </span>
        <FiChevronDown className={jStyles.acctTriggerIcon} size={15} />
      </button>
      {open && (
        <div className={jStyles.acctPanel}>
          {items.map((item) => {
            const isSel = item.id === value;
            const bf = item.bal !== null ? fmtBal(item.bal) : null;
            return (
              <div
                key={item.id}
                className={`${jStyles.acctOption} ${isSel ? jStyles.acctOptionSelected : ''}`}
                onClick={() => { onChange(item.id); setOpen(false); }}
              >
                <span className={jStyles.acctOptionName}>{item.name}</span>
                {!loadingFunds && bf && (
                  <span className={`${jStyles.acctOptionBal} ${bf.side === 'Dr' ? jStyles.acctOptionBalDr : jStyles.acctOptionBalCr}`}>
                    {bf.side} {bf.amount}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------------------

const NewPaymentsPage = () => {
  const {
    activeDerasarId,
    financialYears: authFinancialYears,
    activeFinancialYearId,
  } = useAuth();
  const { preferences } = usePreferences();
  const navigate = useNavigate();

  // Reference data
  const [categories, setCategories]     = useState([]);
  const [financialYears, setFY]         = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [allAccounts, setAllAccounts]   = useState([]);
  const [loadingRef, setLoadingRef]     = useState(true);
  const [fundBalances, setFundBalances] = useState({});
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [balanceMap, setBalanceMap]     = useState({});

  // Form state
  const [voucherDate, setVoucherDate]         = useState(today());
  const [financialYearId, setFinancialYearId] = useState('');
  const [fundSource, setFundSource]           = useState(''); // '' | '__commission__' | <categoryId>
  const [expenseTypeId, setExpenseTypeId]     = useState('');
  const [debitAcctId, setDebitAcctId]         = useState(''); // Expense/Asset/Liability (Dr)
  const [creditAcctId, setCreditAcctId]       = useState(''); // Cash/Bank Asset (Cr)
  const [amount, setAmount]                   = useState('');
  const PAYMENT_MODES_SET = new Set(['cash', 'bank_transfer', 'cheque', 'upi']);
  const prefPM = preferences.defaultPaymentMode === 'bank' ? 'bank_transfer' : preferences.defaultPaymentMode;
  const [paymentMode, setPaymentMode]         = useState(PAYMENT_MODES_SET.has(prefPM) ? prefPM : 'cash');
  const [payeeName, setPayeeName]             = useState('');
  const [chequeNo, setChequeNo]               = useState('');
  const [narration, setNarration]             = useState('');
  const [saving, setSaving]                   = useState(false);

  // Expense type modal
  const [showExpenseTypeModal, setShowExpenseTypeModal] = useState(false);
  const [newExpenseTypeName, setNewExpenseTypeName]     = useState('');

  // -- Derived ---------------------------------------------------------------
  const selectedDebitAcct  = allAccounts.find(a => String(a.id) === String(debitAcctId));
  const selectedCreditAcct = allAccounts.find(a => String(a.id) === String(creditAcctId));
  const totalAmount = parseFloat(amount || 0);
  const selectableFinancialYears = useMemo(
    () => getUnlockedFinancialYears(authFinancialYears || []).filter((financialYear) => !isOpeningFinancialYear(financialYear)),
    [authFinancialYears],
  );
  const selectedFinancialYear = financialYears.find((financialYear) => String(financialYear.id) === String(financialYearId)) || null;
  const minVoucherDate = (selectedFinancialYear?.startDate || '').slice(0, 10);
  const maxVoucherDate = (selectedFinancialYear?.endDate || '').slice(0, 10);
  const selectedFundBalance = fundSource === '__commission__'
    ? (fundBalances.__commission__ || 0)
    : (fundBalances[fundSource] || 0);

  // Journal preview
  const journalLines = (() => {
    if (!selectedDebitAcct || !selectedCreditAcct || totalAmount <= 0) return [];
    return [
      { type: 'debit',  label: selectedDebitAcct.name },
      { type: 'credit', label: selectedCreditAcct.name },
    ];
  })();

  // -- Load reference data ---------------------------------------------------
  useEffect(() => {
    if (!activeDerasarId) return;
    Promise.all([
      getDonationCategories(activeDerasarId),
      Promise.resolve({ data: selectableFinancialYears }),
      getExpenseTypes(activeDerasarId),
      getAllLedgerAccounts(activeDerasarId),
    ])
      .then(([catRes, fyRes, etRes, accRes]) => {
        setCategories(catRes.data);
        setFY(selectableFinancialYears);
        const defaultYear = fyRes.data.find((financialYear) => String(financialYear.id) === String(activeFinancialYearId))
          || getCurrentUnlockedFinancialYear(fyRes.data)
          || fyRes.data[0]
          || null;
        if (defaultYear) setFinancialYearId(String(defaultYear.id));
        setExpenseTypes(etRes.data);

        const accounts = accRes.data.filter(a => a.isActive);
        setAllAccounts(accounts);

        // Default credit: first cash account (system preferred)
        const cashAccounts = accounts.filter(a => a.accountType === 'cash');
        const defaultCredit = cashAccounts.find(a => a.isSystem) || cashAccounts[0] || null;
        if (defaultCredit) setCreditAcctId(defaultCredit.id);
      })
      .catch(() => toast.error('Failed to load form data'))
      .finally(() => setLoadingRef(false));
  }, [activeDerasarId, activeFinancialYearId, selectableFinancialYears]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setFY(selectableFinancialYears);
    if (activeFinancialYearId) setFinancialYearId(String(activeFinancialYearId));
  }, [activeDerasarId, activeFinancialYearId, selectableFinancialYears]);

  useEffect(() => {
    if (!selectedFinancialYear) return;

    setVoucherDate((current) => {
      let nextDate = current || minVoucherDate || maxVoucherDate || today();
      if (minVoucherDate && nextDate < minVoucherDate) nextDate = minVoucherDate;
      if (maxVoucherDate && nextDate > maxVoucherDate) nextDate = maxVoucherDate;
      return nextDate;
    });
  }, [selectedFinancialYear, minVoucherDate, maxVoucherDate]);

  useEffect(() => {
    if (!activeDerasarId || !financialYearId) { setBalanceMap({}); return; }
    getLedgerBalanceMap(activeDerasarId, financialYearId)
      .then((res) => setBalanceMap(res.data.data || res.data || {}))
      .catch(() => setBalanceMap({}));
  }, [activeDerasarId, financialYearId]);

  useEffect(() => {
    if (!activeDerasarId || !financialYearId || !selectedFinancialYear || !voucherDate) {
      setFundBalances({});
      return;
    }

    const reportFromDate = minVoucherDate || undefined;
    const reportToDate = voucherDate || maxVoucherDate || undefined;
    setLoadingFunds(true);

    getCategoryFund({
      derasarId: activeDerasarId,
      yearId: financialYearId,
      fromDate: reportFromDate,
      toDate: reportToDate,
    })
      .then((res) => {
        const nextBalances = {};
        (res.data?.categories || []).forEach((row) => {
          const category = categories.find((item) => item.name === row.category);
          if (category?.id) {
            nextBalances[String(category.id)] = parseFloat(row.closingBalance || 0);
          }
        });
        nextBalances.__commission__ = parseFloat(res.data?.commission?.closingBalance || 0);
        setFundBalances(nextBalances);
      })
      .catch(() => {
        setFundBalances({});
        toast.error('Failed to load fund balances');
      })
      .finally(() => setLoadingFunds(false));
  }, [activeDerasarId, categories, financialYearId, selectedFinancialYear, voucherDate, minVoucherDate, maxVoucherDate]);

  // -- Sync payment mode when credit (cash/bank) account changes ------------
  const handleCreditAcctChange = useCallback((id) => {
    setCreditAcctId(id);
    const acct = allAccounts.find(a => a.id === id);
    if (!acct) return;
    const inferred = modeForAccountType(acct.accountType, paymentMode);
    if (inferred !== paymentMode) setPaymentMode(inferred);
  }, [allAccounts, paymentMode]);

  const handlePaymentModeChange = useCallback((nextMode) => {
    setPaymentMode(nextMode);

    const targetAccountType = nextMode === 'cash' ? 'cash' : 'bank';
    const currentCreditAccount = allAccounts.find((account) => account.id === creditAcctId);

    if (currentCreditAccount?.accountType === targetAccountType) {
      return;
    }

    const preferredAccount = getPreferredCreditAccount(allAccounts, targetAccountType);
    if (preferredAccount) {
      setCreditAcctId(preferredAccount.id);
    }
  }, [allAccounts, creditAcctId]);

  // -- Create expense type ---------------------------------------------------
  const handleCreateExpenseType = async (e) => {
    e.preventDefault();
    if (!newExpenseTypeName.trim()) return;
    try {
      await createExpenseType({ derasarId: activeDerasarId, name: newExpenseTypeName });
      toast.success('Expense type created');
      setNewExpenseTypeName('');
      setShowExpenseTypeModal(false);
      const etRes = await getExpenseTypes(activeDerasarId);
      setExpenseTypes(etRes.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create expense type');
    }
  };

  // -- Submit ----------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedFy = financialYears.find((financialYear) => String(financialYear.id) === String(financialYearId));
    if (!selectedFy || selectedFy.isClosed) return toast.error('Select an unlocked financial year');
    if (minVoucherDate && voucherDate < minVoucherDate) return toast.error('Voucher date must be within the selected financial year');
    if (maxVoucherDate && voucherDate > maxVoucherDate) return toast.error('Voucher date must be within the selected financial year');
    if (!debitAcctId)    return toast.error('Select a debit account (Expense / Asset / Liability)');
    if (!creditAcctId)   return toast.error('Select a credit account (Cash / Bank)');
    if (!fundSource)     return toast.error('Fund Source is required — select a donation category or Commission Fund');
    if (totalAmount <= 0) return toast.error('Amount must be greater than zero');
    if (!narration.trim()) return toast.error('Narration is required');

    setSaving(true);
    try {
      const isCommission = fundSource === '__commission__';
      await createPaymentVoucher({
        derasarId: activeDerasarId,
        financialYearId,
        voucherDate,
        debitLedgerAccountId:  debitAcctId,
        creditLedgerAccountId: creditAcctId,
        donationCategoryId: (!isCommission && fundSource) ? fundSource : undefined,
        isCommission: isCommission || undefined,
        expenseTypeId:      expenseTypeId || undefined,
        amount: parseFloat(amount),
        payeeName: payeeName.trim() || undefined,
        paymentMode,
        chequeNo: paymentMode === 'cheque' ? chequeNo || undefined : undefined,
        narration,
      });
      toast.success('Payment voucher created');
      navigate('/accounting/payments');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create voucher');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRef) return <div className="loading"><div className="spinner" /></div>;

  const openFy = selectedFinancialYear;
  const isSelectedYearUnlocked = !!openFy && !openFy.isClosed;

  return (
    <div className={styles.formPage}>
      <div className="page-header">
        <h2><FiFileText /> New Payment</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/accounting/payments')}>
          <FiArrowLeft /> Back
        </button>
      </div>

      {!isSelectedYearUnlocked && (
        <div className={`${aStyles.infoBox} ${aStyles.infoBoxWarn}`} style={{ marginBottom: 16 }}>
          <FiAlertTriangle /> Select an unlocked financial year before creating vouchers.
        </div>
      )}

      <div className={styles.formScrollArea}>
      <form id="new-payment-form" onSubmit={handleSubmit}>
        <div className="card">

          {/* -- Section 1: Voucher Date + Expense Type -- */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Voucher Details</div>
            <div className={styles.voucherRow}>
              <div className="form-group">
                <label>Voucher Date *</label>
                <input type="date" value={voucherDate} required
                  min={minVoucherDate || undefined}
                  max={maxVoucherDate || undefined}
                  onChange={e => setVoucherDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ width: 220 }}>
                <label>Expense Type <em className={styles.optLabel}>(optional)</em></label>
                <select value={expenseTypeId} onChange={e => setExpenseTypeId(e.target.value)}>
                  <option value="">— None —</option>
                  {expenseTypes.map(et => <option key={et.id} value={et.id}>{et.name}</option>)}
                </select>
              </div>
              <div className={styles.voucherRowBtn}>
                <span className={styles.voucherRowBtnLabel}>‌</span>
                <button type="button" className="btn btn-ghost"
                  onClick={() => setShowExpenseTypeModal(true)}>
                  <FiSettings /> Expense Types
                </button>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          {/* -- Section 2: Accounting Entries -- */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Accounting Entries</div>
            <div className={styles.row3}>
              <div className="form-group">
                <label>Debit Account *</label>
                <LedgerAccountSelect
                  value={debitAcctId}
                  onChange={(id) => setDebitAcctId(id)}
                  accounts={allAccounts}
                  filterFn={(a) => ['expenditure', 'asset', 'liability'].includes(a.group?.nature)}
                  balanceMap={balanceMap}
                  placeholder="— Expense / Asset / Liability —"
                />
                {debitAcctId && (() => { const bf = fmtBal(balanceMap[String(debitAcctId)]); return bf ? <span className={styles.acctBal} style={{ color: bf.color }}>{bf.side} {fmtCurrency(Math.abs(balanceMap[String(debitAcctId)]))}</span> : null; })()}
              </div>

              <div className="form-group">
                <label>Credit Account *</label>
                <LedgerAccountSelect
                  value={creditAcctId}
                  onChange={(id) => handleCreditAcctChange(id)}
                  accounts={allAccounts}
                  filterFn={(a) => ['cash', 'bank'].includes(a.accountType)}
                  balanceMap={balanceMap}
                  placeholder="— Cash / Bank —"
                />
                {creditAcctId && (() => { const bf = fmtBal(balanceMap[String(creditAcctId)]); return bf ? <span className={styles.acctBal} style={{ color: bf.color }}>{bf.side} {fmtCurrency(Math.abs(balanceMap[String(creditAcctId)]))}</span> : null; })()}
              </div>

              <div className="form-group">
                <label>Fund Source *</label>
                <FundSourceSelect
                  value={fundSource}
                  onChange={setFundSource}
                  categories={categories}
                  fundBalances={fundBalances}
                  loadingFunds={loadingFunds}
                />
                {fundSource && !loadingFunds && (() => {
                  const bf = fmtBal(selectedFundBalance);
                  return bf ? <span className={styles.acctBal} style={{ color: bf.color }}>Available {bf.side} {fmtCurrency(Math.abs(selectedFundBalance))}</span> : null;
                })()}
              </div>
            </div>

            {journalLines.length > 0 && totalAmount > 0 && (
              <div className={styles.journalPreview}>
                {journalLines.map(({ type, label }) => (
                  <div key={label} className={styles.journalRow}>
                    <span className={type === 'debit' ? styles.journalDr : styles.journalCr}>
                      {type === 'debit' ? 'Dr' : 'Cr'}
                    </span>
                    <span>{label}</span>
                    <span className={styles.journalAmt}>{fmtCurrency(totalAmount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.divider} />

          {/* -- Section 3: Payment Details -- */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Payment Details</div>
            <div className={styles.paymentRow}>
              <div className="form-group">
                <label>Payment Mode *</label>
                <div className={styles.modeRow}>
                  {PAYMENT_MODES.map(m => (
                    <label key={m.value}
                      className={`${styles.modeChip} ${paymentMode === m.value ? styles.modeChipOn : ''}`}>
                      <input type="radio" name="paymentMode" value={m.value}
                        checked={paymentMode === m.value}
                        onChange={() => handlePaymentModeChange(m.value)} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
              {paymentMode === 'cheque' && (
                <div className="form-group" style={{ width: 150, flexShrink: 0 }}>
                  <label>Cheque No</label>
                  <input value={chequeNo} placeholder="e.g. 001234"
                    onChange={e => setChequeNo(e.target.value)} />
                </div>
              )}
              <div className="form-group" style={{ width: 160, flexShrink: 0 }}>
                <label>Amount (₹) *</label>
                <input type="number" min="0.01" step="0.01" value={amount} required
                  placeholder="Enter amount" className={styles.noSpin} onWheel={blockWheel}
                  onChange={e => setAmount(e.target.value)} />
              </div>
            </div>
            <div className={styles.row2}>
              <div className="form-group">
                <label>Payee Name <em className={styles.optLabel}>(optional)</em></label>
                <input value={payeeName} placeholder="Who was paid"
                  onChange={e => setPayeeName(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Narration *</label>
                <input value={narration} required placeholder="Purpose of payment (required)"
                  onChange={e => setNarration(e.target.value)} />
              </div>
            </div>
          </div>

        </div>
      </form>
      </div>

      <div className={styles.pageFooter}>
        <div className={styles.pageFooterLeft} />
        <div className={styles.pageFooterRight}>
          <div className={styles.totalBox}>
            <span className={styles.totalLabel}>Voucher Amount</span>
            <span className={styles.totalValue}>
              {totalAmount > 0
                ? <>{fmtCurrency(totalAmount)}</>
                : <span className={styles.totalMuted}>—</span>}
            </span>
          </div>
          <div className={styles.actionBtns}>
            <button type="button" className="btn btn-ghost"
              onClick={() => navigate('/accounting/payments')}>
              Cancel
            </button>
            <button type="submit" form="new-payment-form" className="btn btn-primary" disabled={saving || !isSelectedYearUnlocked}>
              {saving ? 'Saving…' : <><FiSave /> Create Voucher</>}
            </button>
          </div>
        </div>
      </div>

      {/* -- Expense Types Modal -- */}
      {showExpenseTypeModal && (
        <div className="modal-overlay" onClick={() => setShowExpenseTypeModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3><FiSettings /> Manage Expense Types</h3></div>
            <div className={aStyles.modalBody}>
              <div className={styles.expenseTypePanel}>
                <div className={styles.expenseTypeList}>
                  {expenseTypes.map((et) => (
                    <div key={et.id} className={styles.expenseTypeItem}>
                      <span className={styles.expenseTypeName}>{et.name}</span>
                    </div>
                  ))}
                  {expenseTypes.length === 0 && (
                    <div className={styles.expenseTypeEmpty}>No expense types yet.</div>
                  )}
                </div>
              </div>
              <form onSubmit={handleCreateExpenseType} className={styles.expenseTypeForm}>
                <input className={styles.expenseTypeInput} placeholder="New expense type name…"
                  value={newExpenseTypeName}
                  onChange={e => setNewExpenseTypeName(e.target.value)} required />
                <button type="submit" className="btn btn-primary btn-sm"><FiPlus /> Add</button>
              </form>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowExpenseTypeModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPaymentsPage;
